"use client"

import type React from "react"
import { useSession } from "next-auth/react"
import { useRouter } from "next/navigation"
import { useState } from "react"
import { cn } from "@/src/lib/utils"

interface Discount {
  enable_discount: boolean
  discount: number
}

interface Props {
  listing: {
    img: string
    id: string
    price: number
    interval: string
    name: string
    size: number
    size_unit: string
    description: string
    discounts?: Discount[]
  }
  placeCutoutAtTop?: boolean
}

const FeaturedListing: React.FC<Props> = ({ listing, placeCutoutAtTop = false }) => {
  const { data: session } = useSession()
  const router = useRouter()
  const [isHovered, setIsHovered] = useState(false)

  const handleClick = () => {
    router.push(`/listings/${listing.id}`)
    if (session?.user.role === "booker") {
      // Additional logic for bookers can go here
    }
  }

  const enabledDiscounts = (listing.discounts || []).filter(
    (d) => d.enable_discount && d.discount > 0
  )

  const maxDiscount = enabledDiscounts.length > 0
    ? Math.max(...enabledDiscounts.map((d) => d.discount))
    : 0

  const originalPrice = listing.price
  const discountedPrice = maxDiscount
    ? originalPrice * (1 - maxDiscount / 100)
    : originalPrice

  return (
    <div
      key={listing.id}
      className="group cursor-pointer"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleClick}
    >
      <figure className="aspect-square w-full relative overflow-hidden">
        <img
          src={listing.img || "/placeholder.svg"}
          alt={listing.description}
          className={cn("object-cover w-full h-full transition-all duration-300", {
            "filter brightness-75": isHovered,
          })}
        />

        {/* Discount Count Badge */}
        {enabledDiscounts.length > 0 && (
          <div className="absolute top-3 left-3 bg-green-500 text-white px-2 py-1 rounded-md text-xs font-semibold z-10">
            {enabledDiscounts.length} Discount{enabledDiscounts.length > 1 ? "s" : ""} Available
          </div>
        )}

        {/* Decorative Corners */}
        <div
          className={cn(
            "flex items-center justify-between absolute left-0 w-full transition-all duration-300",
            {
              "top-0": placeCutoutAtTop,
              "bottom-0": !placeCutoutAtTop,
            }
          )}
        >
          <div className="w-2.5 lg:w-10 aspect-square bg-white"></div>
          <div className="w-2.5 lg:w-10 aspect-square bg-white"></div>
        </div>

        {/* Hover Text */}
        <div
          className={cn(
            "absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-white text-lg md:text-xl lg:text-2xl font-serif font-bold tracking-wide transition-all duration-300",
            {
              "opacity-100": isHovered,
              "opacity-0": !isHovered,
            }
          )}
          style={{ textShadow: "1px 1px 2px rgba(0, 0, 0, 0.5)" }}
        >
          Explore Listing
        </div>
      </figure>

      <div className="mt-5">
        <div className="flex flex-col gap-1.5 text-1xs md:text-sm lg:text-base">
          {/* Name */}
          <p className="text-dark-gray font-semibold text-lg ellipsis-1">
            {listing.name}
          </p>

          {/* Description */}
          <p className="text-gray-secondary text-sm line-clamp-2 md:line-clamp-3">
            {listing.description}
          </p>

          {/* Size & Price */}
          <div className="flex items-center justify-between mt-2.5">
            <span className="text-gray-secondary text-xs md:text-sm">
              {listing.size} {listing.size_unit}
            </span>

            <div className="flex flex-col items-end">
                  <span className="text-gray-600 font-semibold text-xs">
                    NGN {originalPrice.toLocaleString()} / {listing.interval}
                  </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default FeaturedListing
